import { featureFlagsAtom } from '@polaris/core/stores'
import { formatPrice } from '@polaris/core/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu'
import { useAtomValue, useSetAtom } from 'jotai'
import { Check, Eye, EyeClosed, ListFilter } from 'lucide-react'
import { motion } from 'motion/react'
import Image from 'next/image'
import Link from 'next/link'
import { useCallback, useState } from 'react'
import { useTokenActions } from '@/src/hooks/token-info/use-token-actions'
import { useUserSettings } from '@/src/hooks/use-user-settings'
import { useGetReferrerProfile } from '@/src/queries/hooks/referralcodes'
import { walletAccountsAtom } from '@/src/stores/accounts/wallets'
import { accountsFilteredBalancesAtom } from '@/src/stores/balances/accounts'
import { loginDialogOpenAtom } from '@/src/stores/onboarding'
import {
  openSelectWalletsDialog,
  selectWalletsDialogAtom,
} from '@/src/stores/select-wallets'
import { userAtom } from '@/src/stores/user/atoms'
import { cn } from '@/src/utils/styles'
import { GroupedActivity } from '../../activity-screen/grouped-activity'
import { AffiliateCard } from '../../affiliate-card'
import {
  BalanceSummariesAccordions,
  ChainBalancesAccordions,
  WalletGroupsAccordions,
} from '../../balances'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import {
  ListRow,
  ListRowButton,
  ListRowSide,
  ListRowSideName,
} from '../../ui/list-row'
import { Switch } from '../../ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTitle,
  TooltipTrigger,
} from '../../ui/tooltip'
import { FailedTradeNotification } from '../failed-trade-notification'

export function Overview() {
  const user = useAtomValue(userAtom)
  const featureFlags = useAtomValue(featureFlagsAtom)
  const { selectToken, prefetchRoute } = useTokenActions({ direction: 'from' })

  const [selectedTab, setSelectedTab] = useState<'balances' | 'activity'>(
    'balances',
  )
  const [grouping, setGrouping] = useState<
    'tokens' | 'networks' | 'wallets' | 'privacy-vault'
  >('tokens')

  // Add grouped balances by wallets
  const {
    balancesByChains,
    balancesSummary,
    privacyBalancesSummary,
    groupedAccounts,
  } = useAtomValue(accountsFilteredBalancesAtom)
  const { data: referrerProfile } = useGetReferrerProfile()

  // Unified token selection that handles both form updates and navigation
  const handleBalanceClick = useCallback(
    ({
      chain_id,
      denom,
      ticker,
    }: {
      chain_id: string
      denom: string
      ticker: string
    }) => {
      selectToken({ chain_id, denom, ticker })
    },
    [selectToken],
  )

  if (!user) return null

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='flex flex-col gap-6 pb-page-gutter'
    >
      {user.id === 'guest' && groupedAccounts.length > 0 && (
        <GuestCreateAccountCardCtaLink />
      )}
      {groupedAccounts.length > 0 && <BalanceHeader />}
      {groupedAccounts.length > 0 && <FailedTradeNotification />}
      <div className='flex flex-col'>
        {groupedAccounts.length > 0 && (
          <div className='relative flex items-center justify-between gap-3'>
            <div className='flex items-center gap-6'>
              <Button
                onClick={() => setSelectedTab('balances')}
                className='before:rounded-xl my-3'
              >
                <span
                  className={cn({
                    'text-primary': selectedTab === 'balances',
                    'text-tertiary hover:text-secondary':
                      selectedTab === 'activity',
                  })}
                >
                  Balances
                </span>
              </Button>
              <Button
                onClick={() => setSelectedTab('activity')}
                className='before:rounded-xl my-3'
              >
                <span
                  className={cn({
                    'text-primary': selectedTab === 'activity',
                    'text-tertiary hover:text-secondary':
                      selectedTab === 'balances',
                  })}
                >
                  Activity
                </span>
              </Button>
            </div>
            {selectedTab === 'balances' && balancesByChains.length > 0 && (
              <DropdownFilters
                grouping={grouping}
                setGrouping={setGrouping}
                tokenCount={balancesSummary.length}
                privacyTokenCount={privacyBalancesSummary.length}
                networkCount={balancesByChains.length}
              />
            )}
          </div>
        )}
        {selectedTab === 'balances' ? (
          balancesByChains.length > 0 ? (
            <div className='-mx-4'>
              {grouping === 'tokens' ? (
                <BalanceSummariesAccordions
                  onHoverBalance={({ chain_id, denom, ticker }) => {
                    prefetchRoute({
                      chain_id,
                      denom,
                      ticker,
                    })
                  }}
                  onClickBalance={handleBalanceClick}
                />
              ) : null}
              {grouping === 'privacy-vault' ? (
                <BalanceSummariesAccordions
                  balancesSummary={privacyBalancesSummary}
                  onHoverBalance={({ chain_id, denom, ticker }) => {
                    prefetchRoute({
                      chain_id,
                      denom,
                      ticker,
                    })
                  }}
                  onClickBalance={handleBalanceClick}
                />
              ) : null}
              {grouping === 'networks' ? (
                <ChainBalancesAccordions
                  onClickBalance={handleBalanceClick}
                  onHoverBalance={({ chain_id, denom, ticker }) => {
                    prefetchRoute({
                      chain_id,
                      denom,
                      ticker,
                    })
                  }}
                />
              ) : null}
              {grouping === 'wallets' ? (
                <WalletGroupsAccordions
                  walletGroups={groupedAccounts}
                  onClickBalance={handleBalanceClick}
                  onHoverBalance={({ chain_id, denom, ticker }) => {
                    prefetchRoute({
                      chain_id,
                      denom,
                      ticker,
                    })
                  }}
                />
              ) : null}
            </div>
          ) : groupedAccounts.length > 0 ? (
            <NoBalances />
          ) : (
            <NoWallets />
          )
        ) : (
          <GroupedActivity />
        )}

        {featureFlags.referrals &&
        user.id !== 'guest' &&
        groupedAccounts.length > 0 &&
        !referrerProfile?.referral_code ? (
          <AffiliateCard className='mt-6' />
        ) : null}
      </div>
    </motion.div>
  )
}

function BalanceHeader() {
  const { totalBalance } = useAtomValue(accountsFilteredBalancesAtom)
  const [integer, decimals] = formatPrice(totalBalance).split('.')

  const { userSettings, updateSettings } = useUserSettings()

  return (
    <>
      {totalBalance > 0 ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type='button'
                onClick={() =>
                  updateSettings({ maskBalances: !userSettings.maskBalances })
                }
                className='group/balance flex items-center gap-3 min-h-12 sm:min-h-14 mt-6 xl:mt-0'
              >
                <h3
                  className={cn(
                    'text-4xl sm:text-5xl text-tertiary font-extralight font-serif',
                    {
                      'h-4': userSettings.maskBalances,
                    },
                  )}
                >
                  {!userSettings.maskBalances ? (
                    <>
                      <span className='text-primary'>{integer}</span>.
                      {decimals || '00'}
                    </>
                  ) : (
                    <span className='text-tertiary'>* * * * *</span>
                  )}
                </h3>
                <Badge
                  size='large'
                  variant='muted'
                  leftIcon={
                    userSettings.maskBalances ? (
                      <EyeClosed size={20} />
                    ) : (
                      <Eye size={20} />
                    )
                  }
                  className={cn(
                    'transition-opacity',
                    userSettings.maskBalances
                      ? 'opacity-100'
                      : 'opacity-0 group-hover/balance:opacity-100',
                  )}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent align='end' alignOffset={-32}>
              <div className='flex items-center gap-3'>
                <div className='flex-flex-col gap-1'>
                  <TooltipTitle>
                    {userSettings.maskBalances
                      ? 'Show balances'
                      : 'Hide balances'}
                  </TooltipTitle>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <h3 className='text-4xl sm:text-5xl text-tertiary font-extralight font-serif mt-6 xl:mt-0'>
          $0.00
        </h3>
      )}
    </>
  )
}

function DropdownFilters({
  grouping,
  setGrouping,
  tokenCount,
  privacyTokenCount,
  networkCount,
}: {
  grouping: 'tokens' | 'networks' | 'wallets' | 'privacy-vault'
  setGrouping: (
    grouping: 'tokens' | 'networks' | 'wallets' | 'privacy-vault',
  ) => void
  tokenCount: number
  privacyTokenCount: number
  networkCount: number
}) {
  const { userSettings, updateSettings, onSmallestBalanceInput } =
    useUserSettings()
  const [smallestBalanceUsd, setSmallestBalanceUsd] = useState(
    userSettings.smallestBalanceUsd.toString(),
  )
  const { groupedAccounts } = useAtomValue(walletAccountsAtom)
  const { privacy } = useAtomValue(featureFlagsAtom)

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value.trim()
      const cleanedInput = onSmallestBalanceInput(input)
      if (cleanedInput !== null) {
        setSmallestBalanceUsd(cleanedInput)
      }
    },
    [onSmallestBalanceInput],
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type='button' variant='ghost' size='md'>
          <ListFilter className='text-link size-6' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className='bg-foreground border border-border1 bg-clip-padding shadow-2xl rounded-2xl flex flex-col w-64 -translate-x-4 animate-enter-scale origin-top'
        sideOffset={10}
      >
        <div className='flex flex-col px-2 py-2 border-b border-border1'>
          <span className='py-2 px-3 text-secondary text-sm'>Group by</span>
          <DropdownMenuItem asChild>
            <ListRowButton
              type='button'
              size='sm'
              onClick={() => setGrouping('tokens')}
            >
              <ListRowSide>
                <ListRowSideName className='!text-base'>Tokens</ListRowSideName>
              </ListRowSide>
              <ListRowSide>
                {grouping === 'tokens' ? (
                  <Check className='size-5 text-link' />
                ) : (
                  <span className='text-tertiary-100 text-sm'>
                    {tokenCount}
                  </span>
                )}
              </ListRowSide>
            </ListRowButton>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <ListRowButton size='sm' onClick={() => setGrouping('networks')}>
              <ListRowSide>
                <ListRowSideName className='!text-base'>
                  Networks
                </ListRowSideName>
              </ListRowSide>
              <ListRowSide>
                {grouping === 'networks' ? (
                  <Check className='size-5 text-link' />
                ) : (
                  <span className='text-tertiary-100 text-sm'>
                    {networkCount}
                  </span>
                )}
              </ListRowSide>
            </ListRowButton>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <ListRowButton size='sm' onClick={() => setGrouping('wallets')}>
              <ListRowSide>
                <ListRowSideName className='!text-base'>
                  Wallets
                </ListRowSideName>
              </ListRowSide>
              <ListRowSide>
                {grouping === 'wallets' ? (
                  <Check className='size-5 text-link' />
                ) : (
                  <span className='text-tertiary-100 text-sm'>
                    {groupedAccounts.length}
                  </span>
                )}
              </ListRowSide>
            </ListRowButton>
          </DropdownMenuItem>
          {privacy ? (
            <DropdownMenuItem asChild>
              <ListRowButton
                size='sm'
                onClick={() => setGrouping('privacy-vault')}
              >
                <ListRowSide>
                  <ListRowSideName className='!text-base'>
                    Privacy Vault
                  </ListRowSideName>
                </ListRowSide>
                <ListRowSide>
                  {grouping === 'privacy-vault' ? (
                    <Check className='size-5 text-link' />
                  ) : (
                    <span className='text-tertiary-100 text-sm'>
                      {privacyTokenCount}
                    </span>
                  )}
                </ListRowSide>
              </ListRowButton>
            </DropdownMenuItem>
          ) : null}
        </div>
        <div className='flex flex-col px-2 py-2'>
          <DropdownMenuItem asChild>
            <ListRow size='sm' onClick={(e) => e.preventDefault()}>
              <ListRowSide>
                <ListRowSideName className='!text-base'>
                  Hide small balances
                </ListRowSideName>
              </ListRowSide>
              <Switch
                checked={userSettings.hideSmallBalances}
                onCheckedChange={(value) => {
                  updateSettings({
                    hideSmallBalances: value,
                  })
                }}
              />
            </ListRow>
          </DropdownMenuItem>
          <div className='flex items-center justify-between w-full py-2 px-3'>
            <ListRowSide>
              <ListRowSideName className='!text-base'>
                Hide below
              </ListRowSideName>
            </ListRowSide>
            <Input
              value={smallestBalanceUsd}
              dimension='small'
              type='text'
              containerClassName='max-w-[62px]'
              onChange={handleOnChange}
              onBlur={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              className='text-right'
            />
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function NoBalances() {
  const setDialogState = useSetAtom(selectWalletsDialogAtom)

  return (
    <div className='pt-8 pb-16 flex flex-col gap-4 items-center'>
      <Image
        src='/assets/cactus.png'
        alt='Cactus in desert'
        width={240}
        height={240}
      />
      <div className='flex flex-col gap-3 text-center'>
        <span className='text-4xl text-primary font-serif font-extralight'>
          Nothing to see here
        </span>
        <span className='text-secondary'>
          You have no balances. Send funds to your wallet or add another wallet
          to see your balances here.
        </span>
        <Button
          className='mt-6'
          onClick={() => {
            setDialogState(openSelectWalletsDialog())
          }}
        >
          Add wallet
        </Button>
      </div>
    </div>
  )
}

function NoWallets() {
  const user = useAtomValue(userAtom)
  const setDialogState = useSetAtom(selectWalletsDialogAtom)

  if (!user) return null

  return (
    <div className='px-2 pt-8 pb-16 flex flex-col items-center'>
      <Image
        src={
          user.id !== 'guest'
            ? 'https://s3.polaris.app/illustrations/starter-kit-2.png'
            : 'https://s3.polaris.app/illustrations/guest-starter-kit-2.png'
        }
        alt='Backpack'
        width={280}
        height={280}
      />
      <div className='flex flex-col gap-3 text-center items-center'>
        <span className='text-4xl text-primary font-serif font-extralight'>
          {user.id !== 'guest' ? (
            `Welcome${user?.name ? `, ${user.name}` : ' to Polaris'}`
          ) : (
            <>
              You’re in <span className='italic'>guest</span> mode
            </>
          )}
        </span>
        <span className='text-secondary'>
          {user.id !== 'guest' ? (
            'You’re ready to build your portfolio by adding your wallets.'
          ) : (
            <>
              Wallets you use in <span className='italic'>guest</span>
              {'  '}mode won’t be added to your account’s portfolio.
            </>
          )}
        </span>
        <Button
          variant='primary'
          className='mt-3'
          onClick={() => {
            setDialogState(openSelectWalletsDialog())
          }}
        >
          Add wallet
        </Button>
      </div>
    </div>
  )
}

function GuestCreateAccountCardCtaLink() {
  const featureFlags = useAtomValue(featureFlagsAtom)
  const setLoginDialogOpen = useSetAtom(loginDialogOpenAtom)

  const content = (
    <>
      <div className='flex items-center justify-center basis-20'>
        <Image
          src='/assets/backpack.png'
          alt='Backpack'
          width={72}
          height={72}
          className='min-w-20 w-full h-auto'
          priority
        />
      </div>
      <div className='flex flex-col gap-1 text-left grow'>
        <span className='text-base font-semibold text-primary'>
          Save your portfolio
        </span>
        <span className='text-sm text-secondary'>
          Keep track of all your wallets, everywhere with a Polaris account.
        </span>
      </div>
    </>
  )

  if (featureFlags.newLoginFlow) {
    return (
      <button
        type='button'
        onClick={() => setLoginDialogOpen(true)}
        className='light justify-between w-full bg-green-200 hover:bg-orange-200 transition rounded-2xl flex items-center gap-3 p-4 mt-4 xl:mt-0 xl:mb-4 focus-outer'
      >
        {content}
      </button>
    )
  }

  return (
    <Link
      href='/login'
      className='light justify-between w-full bg-green-200 hover:bg-orange-200 transition rounded-2xl flex items-center gap-3 p-4 mt-4 xl:mt-0 xl:mb-4 focus-outer'
    >
      {content}
    </Link>
  )
}
